/**
 * Link do banner "Developed by Multipedidos" do rodapé do cardápio.
 *
 * Função PURA e fora do componente de propósito: a URL é um contrato com o
 * marketing (os UTMs alimentam a aquisição no GA da multipedidos.com.br) e um
 * contrato com a nossa telemetria (o mesmo `slot`/`variant` que vai no evento
 * vai no `utm_content`). Contrato tem de ser testável sem TestBed.
 *
 * Formato acordado com o marketing (02/09/2026):
 *
 *   ?utm_source=Pedir.Delivery
 *   &utm_medium=SoftwareMP
 *   &utm_id=Rodape+Pedir.Delivery
 *   &utm_campaign=<slug da loja>
 *
 * Detalhes que parecem cosméticos e não são:
 *
 *  - `utm_id` sai com `+`, LITERAL, como foi especificado. `+` em query string
 *    decodifica para espaço, então o GA lê "Rodape Pedir.Delivery" — que é o
 *    rótulo desejado. Passar isso por `encodeURIComponent` viraria `%2B` e o
 *    relatório mostraria o sinal de mais no meio do nome.
 *  - a `slug` (e SÓ ela) é escapada: é dado vindo do banco (`restaurant_url`) e
 *    um caractere reservado ali quebraria a query inteira.
 *  - sem slug, `utm_campaign` é OMITIDO em vez de sair vazio: uma campanha ''
 *    vira uma linha "(not set)" no relatório, indistinguível de erro de tagging.
 *  - `utm_content` distingue os DOIS gestos do rodapé (a logo e o botão de
 *    texto), que hoje apontam para o mesmo lugar. É o que permite responder
 *    "as pessoas clicam na imagem ou na chamada?" do lado do marketing — e é o
 *    mesmo valor que a telemetria manda em `in.slot`, para os dois lados
 *    baterem.
 */

/** Qual dos dois elementos clicáveis do rodapé. */
export type FooterBannerSlot = 'logo' | 'cta';

/**
 * Variante do teste A/B de landing page que já existia no componente (80/20).
 * O nome é o da LP, não "A"/"B": em seis meses ninguém lembra qual era qual.
 */
export type FooterBannerVariant = 'nv-preco' | 'lp-nova-versao';

/** Landing page de cada variante. Sem barra final: ela entra aqui. */
const LANDING: { [k in FooterBannerVariant]: string } = {
  'nv-preco': 'https://multipedidos.com.br/pedirdelivery-nv-preco/',
  'lp-nova-versao': 'https://multipedidos.com.br/lp-nova-versao-teste/',
};

/** Texto da chamada de cada variante (o que o consumidor lê). */
export const FOOTER_BANNER_TEXT: { [k in FooterBannerVariant]: string } = {
  'nv-preco': 'Conheça a Multipedidos',
  'lp-nova-versao': 'Crie seu Cardápio Também',
};

/**
 * Sorteia a variante do A/B — 80% `nv-preco`, 20% `lp-nova-versao`.
 *
 * `rnd` injetável para o teste não depender de `Math.random`. O sorteio segue
 * POR PAGEVIEW (era assim antes desta mudança e continua sendo): estabilizá-lo
 * por device mudaria o experimento do marketing, o que não é decisão de
 * telemetria. A análise não sofre porque a variante viaja DENTRO de cada
 * evento — a razão clique/impressão é calculada por variante, não por pessoa.
 */
export function pickFooterBannerVariant(rnd: number = Math.random()): FooterBannerVariant {
  return rnd < 0.8 ? 'nv-preco' : 'lp-nova-versao';
}

/**
 * Monta a URL final do banner.
 *
 * @param variant landing page sorteada
 * @param slug    `restaurant.info.restaurant_url` (ex.: `viaroma`)
 * @param slot    qual elemento do rodapé leva a este link
 */
export function buildFooterBannerLink(
  variant: FooterBannerVariant,
  slug: string,
  slot: FooterBannerSlot,
): string {
  const limpo = (slug || '').toString().trim();
  const params = [
    'utm_source=Pedir.Delivery',
    'utm_medium=SoftwareMP',
    // literal: ver o comentário do cabeçalho sobre o `+`.
    'utm_id=Rodape+Pedir.Delivery',
  ];
  if (limpo) params.push('utm_campaign=' + encodeURIComponent(limpo));
  params.push('utm_content=' + slot);
  return LANDING[variant] + '?' + params.join('&');
}
